import { defineStore } from 'pinia';
import { reactive } from 'vue';
import {
  ENTITY_TYPE,
  EntityRequestData,
  EntityResourceContainer,
  EntityStore,
} from '@/features/EntityManager/types';
import { Role, useClientStore } from '@/features/Clients';
import { getIsoDateString, isDate } from '@/utils/dates/dates';
import { VglError } from '@/services/errorHandler/vglError';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { getCurrentSpartenConfig } from '@/utils/getCurrentSpartenConfig/getCurrentSpartenConfig';
import get from 'lodash/get';

export const useEntityManager = defineStore('EntityManager', () => {
  const stores: {
    [key in ENTITY_TYPE]: EntityStore<any>;
  } = reactive({
    [ENTITY_TYPE.CLIENT]: useClientStore(),
  });

  const prepareEntitiesForBackendRequest = (
    includedEntityTypes: ENTITY_TYPE[],
  ): EntityRequestData<any> => {
    const entityRequestData: EntityRequestData<any> = {};

    includedEntityTypes.forEach((type) => {
      const store = stores[type];

      if (!('getEntityRequestData' in store)) {
        throw new VglError(
          VGL_ERROR_CODE.ENTITY_MANAGER_MISSING_STORE_FUNCTION,
          {
            additionalData: `Missing getEntityRequestDate in ${type}`,
          },
        );
      }

      const entities = [
        ...(store.getEntityRequestData as Array<any>),
      ] as EntityResourceContainer<any>[];

      // Make sure we transform Date objects to iso date strings
      entities.forEach((entity) => {
        const parsedData: EntityResourceContainer['data'] = {};

        // format Dates into iso strings
        Object.entries(entity.data).forEach(([key, v]) => {
          parsedData[key] = isDate(v) ? getIsoDateString(v) : v;
        });

        // omit `isLoading` from entity
        const { isLoading, isDirty, ...rest } = entity;
        rest.data = parsedData;

        entityRequestData[rest.refId] = rest;
      });
    });

    return entityRequestData;
  };

  const prepareCalculationRelevantEntityData = () => {
    let { calculationRelevantEntityFields } = getCurrentSpartenConfig();

    if (typeof calculationRelevantEntityFields === 'function') {
      calculationRelevantEntityFields = calculationRelevantEntityFields();
    }

    const entityData: Record<string, Record<string, unknown>> = {};

    Object.entries(calculationRelevantEntityFields).forEach(
      ([entityType, fieldDefinition]) => {
        const store = stores[entityType as ENTITY_TYPE];

        if (!('getDataByRole' in store)) {
          throw new VglError(
            VGL_ERROR_CODE.ENTITY_MANAGER_MISSING_STORE_FUNCTION,
            {
              additionalData: `Missing getDataByRole in ${entityType}`,
            },
          );
        }

        const getDataByRole = store.getDataByRole as (role: Role) => any;

        Object.entries(fieldDefinition).forEach(([role, relevantPaths]) => {
          const data = getDataByRole(role as Role);
          if (data === undefined) return;

          entityData[`${entityType}_${role}`] = {};

          relevantPaths.forEach((path) => {
            entityData[`${entityType}_${role}`][path] = get(data, path);
          });
        });
      },
    );

    return entityData;
  };

  return {
    stores,
    prepareEntitiesForBackendRequest,
    prepareCalculationRelevantEntityData,
  };
});
